<template>
  <div v-if="items.length" class="app-progress-container">
    <!-- Progress Notification Card -->
    <v-card
      v-for="item in items"
      :key="item.id"
      class="app-progress-card"
      elevation="6"
      rounded="lg"
      role="button"
      tabindex="0"
      @click="openMenu"
      @mouseenter="pauseDismiss(item.id, 'hover')"
      @mouseleave="resumeDismiss(item.id, 'hover')"
      @focusin="pauseDismiss(item.id, 'focus')"
      @focusout="resumeDismissIfOutside($event, item.id)"
      @keydown.enter.prevent="openMenu"
      @keydown.space.prevent="openMenu"
    >
      <!-- Progress Header -->
      <div class="app-progress-card__header">
        <v-icon class="mt-1" :icon="stateIcon(item)" :color="stateColor(item)" size="18" />

        <div class="app-progress-card__content">
          <strong class="app-progress-title" :title="item.label">{{ item.label }}</strong>
        </div>

        <v-btn-icon icon="mdi-window-minimize" size="x-small" @click.stop="dismissProgress(item.id)" />
      </div>

      <!-- Progress Summary -->
      <div class="app-progress-card__summary">
        <div class="app-progress-card__meta">
          <span class="text-body-2">{{ stateLabel(item.state) }}</span>
          <span class="text-caption text-medium-emphasis">{{ $t('AppProgressNotificationsRuntime') }}</span>
          <span class="text-caption">{{ runtimeLabel(item) }}</span>
        </div>

        <div class="app-progress-card__summary-progress">
          <span class="text-caption">{{ progressLabel(item) }}</span>
          <v-chip size="small" :color="stateColor(item)" variant="tonal"> {{ item.percent }}% </v-chip>
        </div>
      </div>

      <div class="text-caption text-medium-emphasis text-truncate text-sm">
        {{ item.step }}
      </div>

      <v-progress-linear class="mt-2" :model-value="item.percent" :color="stateColor(item)" rounded />
    </v-card>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import { useNow } from '@vueuse/core'
import { useProgressNotificationsMenu } from '~/composables/useProgressNotificationsMenu'
import {
  getProgressRuntimeLabel,
  getProgressStateColor,
  getProgressStateIcon,
  getProgressStateLabel,
  getProgressValueLabel,
  Progress,
  useVisibleProgressNotifications,
} from '~/lib/notify'
import type { AppProgressNotificationRecord } from '~/lib/notify'

export default defineComponent({
  name: 'AppProgressNotifications',
  setup() {
    const items = useVisibleProgressNotifications()
    const { open: openMenu } = useProgressNotificationsMenu()
    const now = useNow({ interval: 1000 })
    const itemsRecent = computed(() => items.value.toReversed().slice(0, 3)) // three most recent

    function dismissProgress(id: string) {
      Progress.dismiss(id)
    }

    function pauseDismiss(id: string, reason: 'hover' | 'focus') {
      Progress.pauseDismiss(id, reason)
    }

    function resumeDismiss(id: string, reason: 'hover' | 'focus') {
      Progress.resumeDismiss(id, reason)
    }

    function resumeDismissIfOutside(event: FocusEvent, id: string) {
      const currentTarget = event.currentTarget
      const relatedTarget = event.relatedTarget
      if (
        currentTarget instanceof HTMLElement &&
        relatedTarget instanceof Node &&
        currentTarget.contains(relatedTarget)
      ) {
        return
      }
      resumeDismiss(id, 'focus')
    }

    function stateColor(item: AppProgressNotificationRecord) {
      return getProgressStateColor(item)
    }

    function stateIcon(item: AppProgressNotificationRecord) {
      return getProgressStateIcon(item)
    }

    function stateLabel(state: string) {
      return getProgressStateLabel(state)
    }

    function progressLabel(item: AppProgressNotificationRecord) {
      return getProgressValueLabel(item)
    }

    function runtimeLabel(item: AppProgressNotificationRecord) {
      return getProgressRuntimeLabel(item, now.value)
    }

    return {
      items: itemsRecent,
      dismissProgress,
      pauseDismiss,
      resumeDismiss,
      resumeDismissIfOutside,
      openMenu,
      stateColor,
      stateIcon,
      stateLabel,
      progressLabel,
      runtimeLabel,
    }
  },
})
</script>

<style lang="scss" scoped>
.app-progress-container {
  position: fixed;
  left: 16px;
  bottom: 16px;
  z-index: 9999;
  width: min(420px, calc(100vw - 32px));
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
}

.app-progress-card {
  padding: 12px;
  pointer-events: auto;
  cursor: pointer;
}

.app-progress-card__header {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;
  align-items: start;
}

.app-progress-card__content {
  min-width: 0;
}

.app-progress-card__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-top: 12px;
}

.app-progress-card__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
}

.app-progress-card__summary-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
}

.app-progress-title {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  line-height: 1.35;
}
</style>
